import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createCampaign } from '../api/campaigns';
import {
  fetchFactionProfiles,
  fetchReadiness,
  fetchTerrains,
  type FactionProfileView,
  type TerrainView,
} from '../api/catalog';
import type { PlayableServer } from '../api/servers';
import { ca } from '../i18n/ca';
import { ServerPicker } from './ServerPicker';
import { Shell } from './Shell';
import { errorText } from './useSessionLive';

const DEFAULT_ALLOW = [
  'arifle_',
  'hgun_',
  'srifle_',
  'SMG_',
  'U_',
  'V_',
  'B_',
  'H_',
  'G_',
  'Item',
  'FirstAidKit',
  'Medikit',
  '30Rnd_',
  '20Rnd_',
  '16Rnd_',
  '11Rnd_',
  'Binocular',
  'Rangefinder',
];

/**
 * A campaign is the quick start's step 1 plus how the group runs it; chapters
 * come from the board. Step 1 is the server the campaign is played on: every
 * chapter is generated with what that server runs on the day (2026-09-19).
 */
export function NewCampaign() {
  const navigate = useNavigate();
  const [server, setServer] = useState<PlayableServer | null>(null);
  const [hash, setHash] = useState<string | null>(null);
  const [scanned, setScanned] = useState(false);
  const [terrains, setTerrains] = useState<TerrainView[]>([]);
  const [factions, setFactions] = useState<FactionProfileView[]>([]);
  const [world, setWorld] = useState('');
  const [enemy, setEnemy] = useState<number | null>(null);
  const [title, setTitle] = useState('');
  const [players, setPlayers] = useState(12);
  const [nextBy, setNextBy] = useState<'creator' | 'vote'>('vote');
  const [gear, setGear] = useState(true);
  const [allow, setAllow] = useState(DEFAULT_ALLOW.join('\n'));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!server) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchReadiness(server.id);
        if (cancelled) return;
        setHash(r.modSetHash);
        setScanned(r.scanned);
        setTerrains(await fetchTerrains(server.id));
        setFactions(r.scanned ? await fetchFactionProfiles(r.modSetHash) : []);
      } catch {
        if (!cancelled) setTerrains([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [server]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!world) return;
    setBusy(true);
    try {
      const c = await createCampaign({
        title,
        worldClass: world,
        modSetHash: hash,
        panelServerId: server?.id ?? null,
        playerSide: 'west',
        playerFaction: null,
        enemyProfileId: enemy,
        players,
        nextDecidedBy: nextBy,
        gearAllow: gear
          ? allow
              .split('\n')
              .map((s) => s.trim())
              .filter(Boolean)
          : null,
        seed: null,
      });
      navigate(`/c/${c.code}`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell title={ca.newCampaign} width="narrow">
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <form className="card form" onSubmit={submit} aria-label={ca.newCampaign}>
        <label>
          {ca.campaignTitle}
          <input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={80} />
        </label>
        <ServerPicker value={server?.id ?? null} onChange={setServer} />
        {server && !scanned && (
          <p className="small warn">
            {ca.missionUi.server.notScanned} <Link to="/catalog">{ca.catalog}</Link>
          </p>
        )}
        <label>
          {ca.pickTerrain}
          <select
            value={world}
            onChange={(e) => setWorld(e.target.value)}
            disabled={!server}
            required
          >
            <option value="">—</option>
            {terrains.map((t) => (
              <option key={t.id} value={t.worldName}>
                {t.displayName ?? t.worldName}
              </option>
            ))}
          </select>
        </label>
        <label>
          {ca.pickEnemy}
          <select
            value={enemy ?? ''}
            onChange={(e) => setEnemy(e.target.value ? Number(e.target.value) : null)}
            disabled={!scanned}
          >
            <option value="">—</option>
            {factions
              .filter((f) => f.side !== 'civilian')
              .map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name} · {ca.sideNames[f.side] ?? f.side}
                </option>
              ))}
          </select>
        </label>
        <label>
          {ca.campaignPlayers}
          <input
            type="number"
            min={2}
            max={64}
            value={players}
            onChange={(e) => setPlayers(Number(e.target.value))}
          />
        </label>
        <label>
          {ca.nextDecidedBy}
          <select value={nextBy} onChange={(e) => setNextBy(e.target.value as 'creator' | 'vote')}>
            {Object.entries(ca.nextDecidedByNames).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label className="row">
          <input type="checkbox" checked={gear} onChange={(e) => setGear(e.target.checked)} />
          {ca.gearPersistence}
        </label>
        {gear && (
          <label>
            {ca.gearAllow}
            <textarea value={allow} onChange={(e) => setAllow(e.target.value)} rows={5} />
          </label>
        )}
        <div className="row actions">
          <span className="grow" />
          <button type="submit" className="primary" disabled={busy || !world}>
            {ca.createCampaign}
          </button>
        </div>
      </form>
    </Shell>
  );
}
